import IconButton from '@mui/material/IconButton';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ReplayIcon from '@mui/icons-material/Replay';
import CloseIcon from '@mui/icons-material/CloseRounded';
import DeleteIcon from '@mui/icons-material/DeleteRounded';
import { Typography } from '@components/Typography';
import ActionIconButton from '@components/ActionIconButton';
import { POST_STATUS, STORY_EXPIRY_HOURS, isStoryPlatform, normalizePlatform } from '@constants/social-posts';
import PlatformIcon from './PlatformIcon';
import PostStatusChip from './PostStatusChip';
import { EntityChip } from '@components/EntityChips';
import { COLUMN_WIDTHS } from './columns';

// One context action per status: Failed → retry, Scheduled → cancel, Published → delete.
const renderActionButton = (post, handlers, canModify, t) => {
    if (!canModify) return null;

    switch (post.status) {
        case POST_STATUS.FAILED:
            return (
                <ActionIconButton title={t('SocialPosts.retry')} onClick={() => handlers.onRetry(post)}>
                    <ReplayIcon viewBox="0 0 22 22" />
                </ActionIconButton>
            );
        case POST_STATUS.SCHEDULED:
            return (
                <ActionIconButton title={t('common.cancel')} onClick={() => handlers.onCancelDelete(post, 'cancel')}>
                    <CloseIcon viewBox="0 0 22 22" />
                </ActionIconButton>
            );
        case POST_STATUS.PUBLISHED:
            // The API's per-post canDelete mirrors the backend publisher's SupportsDelete (e.g.
            // TikTok can't remove a published post), so there's no hard-coded list to drift.
            if (!post.canDelete) return null;
            return (
                <ActionIconButton title={t('common.delete')} onClick={() => handlers.onCancelDelete(post, 'delete')}>
                    <DeleteIcon viewBox="0 0 22 22" />
                </ActionIconButton>
            );
        default:
            return null;
    }
};

const isStoryExpired = post =>
    isStoryPlatform(normalizePlatform(post.platform)) &&
    post.status === POST_STATUS.PUBLISHED &&
    !!post.completedAt &&
    Date.now() - new Date(post.completedAt).getTime() > STORY_EXPIRY_HOURS * 60 * 60 * 1000;

const storyLinkTitle = (post, t) => {
    if (!isStoryPlatform(normalizePlatform(post.platform))) return t('SocialPosts.openPublishedPost');
    return isStoryExpired(post) ? t('SocialPosts.storyExpired') : t('SocialPosts.storyExpiry');
};

export const buildCollapsibleRows = (posts, locations, handlers, t, canModify = true) => {
    if (!posts?.length) return [];

    const locationLabel = locationId => {
        // Callers pass either account.companies (keyed by companyId) or pre-mapped { id, label }.
        const loc = locations.find(l => l.companyId === locationId || l.id === locationId);
        return loc?.internalName ?? loc?.companyName ?? loc?.label ?? `#${locationId}`;
    };

    return posts.map(post => [
        // Spacer matching the parent's expand-toggle column.
        {
            component: (
                <IconButton style={{ opacity: 0, cursor: 'auto' }} disableRipple>
                    <KeyboardArrowUpIcon />
                </IconButton>
            ),
            width: COLUMN_WIDTHS.expand,
        },
        {
            component: (
                <Typography variant="body2" align="left" sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ width: 18, height: 18, flexShrink: 0 }}>
                        <PlatformIcon platform={post.platform} size={16} />
                    </span>
                    {post.profileName && <span>{post.profileName}</span>}
                </Typography>
            ),
            width: COLUMN_WIDTHS.title,
        },
        {
            component: (
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
                    <EntityChip label={locationLabel(post.locationId)} />
                </div>
            ),
            width: COLUMN_WIDTHS.locations,
        },
        // Empty spacers for the campaign's Platforms + Scheduled time columns.
        { component: <></>, width: COLUMN_WIDTHS.platforms },
        { component: <></>, width: COLUMN_WIDTHS.scheduledTime },
        {
            component: (
                <PostStatusChip
                    status={post.status}
                    t={t}
                    failReason={post.failReason}
                    failReasonCode={post.failReasonCode}
                    platform={post.platform}
                />
            ),
            width: COLUMN_WIDTHS.status,
        },
        {
            component: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
                    {post.postUrl && (
                        <ActionIconButton
                            title={storyLinkTitle(post, t)}
                            onClick={() => window.open(post.postUrl, '_blank', 'noopener,noreferrer')}
                        >
                            <OpenInNewIcon
                                viewBox="0 0 24 24"
                                style={isStoryExpired(post) ? { opacity: 0.45 } : undefined}
                            />
                        </ActionIconButton>
                    )}
                    {renderActionButton(post, handlers, canModify, t)}
                </div>
            ),
            width: COLUMN_WIDTHS.actions,
        },
    ]);
};
